import api from "../api/axios";
import ENDPOINTS from "../api/endpoints";

const SESSION_EXPIRED_MESSAGE =
  "Your session expired due to inactivity. Please log in again to continue.";

const LAST_ACTIVITY_KEY = "lastActivityAt";

const decodeJwtPayload = (token) => {
  if (!token) return null;

  try {
    const parts = token.split(".");

    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
};

export const register = async (payload) => {
  const response = await api.post(ENDPOINTS.AUTH.REGISTER, payload);
  return response.data;
};

export const login = async (payload) => {
  const response = await api.post(ENDPOINTS.AUTH.LOGIN, payload, {
    skipAuthRefresh: true,
  });
  return response.data;
};

export const refreshSession = async () => {
  const refreshToken = getStoredRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available.");
  }

  const response = await api.post(
    ENDPOINTS.AUTH.REFRESH,
    { refreshToken },
    { skipAuthRefresh: true }
  );

  const data = response.data;

  if (data?.statusCode !== 200) {
    throw new Error(data?.message || "Session refresh failed.");
  }

  persistLogin(data);
  return data;
};

export const logout = async () => {
  const refreshToken = getStoredRefreshToken();

  try {
    if (refreshToken) {
      await api.post(
        ENDPOINTS.AUTH.LOGOUT,
        { refreshToken },
        { skipAuthRefresh: true }
      );
    }
  } catch {
    // swallow backend logout errors; frontend still clears local auth state
  } finally {
    clearAuth();
  }
};

export const verifyEmail = async (token) => {
  const response = await api.get(ENDPOINTS.AUTH.VERIFY_EMAIL, {
    params: { token },
  });
  return response.data;
};

export const resendVerification = async (email) => {
  const response = await api.post(ENDPOINTS.AUTH.RESEND_VERIFICATION, null, {
    params: { email },
  });
  return response.data;
};

export const forgotPassword = async (payload) => {
  const response = await api.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, payload);
  return response.data;
};

export const resetPassword = async (payload) => {
  const response = await api.post(ENDPOINTS.AUTH.RESET_PASSWORD, payload);
  return response.data;
};

export const persistLogin = (data) => {
  const token = data?.token || "";
  const refreshToken = data?.refreshToken || "";
  const role = data?.role || "";
  const user = data?.user || null;

  if (token) {
    localStorage.setItem("token", token);
  }

  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }

  if (role) {
    localStorage.setItem("role", role);
  }

  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  }

  touchSessionActivity();
};

export const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("role");
  localStorage.removeItem("user");
  localStorage.removeItem(LAST_ACTIVITY_KEY);
};

export const getStoredToken = () => localStorage.getItem("token");
export const getStoredRefreshToken = () => localStorage.getItem("refreshToken");
export const getStoredRole = () => localStorage.getItem("role");

export const getStoredUser = () => {
  const raw = localStorage.getItem("user");
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getTokenExpiryMs = (token) => {
  if (!token) return 0;

  const payload = decodeJwtPayload(token);

  if (!payload?.exp) return 0;

  return payload.exp * 1000;
};

export const isTokenExpired = (token) => {
  if (!token) return true;

  const expiresAt = getTokenExpiryMs(token);

  if (!expiresAt) return false;

  return expiresAt <= Date.now();
};

export const willTokenExpireSoon = (token, thresholdMs = 2 * 60 * 1000) => {
  if (!token) return true;

  const expiresAt = getTokenExpiryMs(token);

  if (!expiresAt) return false;

  return expiresAt - Date.now() <= thresholdMs;
};

export const hasValidSession = () => {
  const token = getStoredToken();

  if (!token) return false;

  return !isTokenExpired(token);
};

export const touchSessionActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

export const getLastActivityAt = () => {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  return raw ? Number(raw) : 0;
};

export const isIdleTimedOut = (idleTimeoutMs) => {
  const lastActivityAt = getLastActivityAt();

  if (!lastActivityAt) return false;

  return Date.now() - lastActivityAt >= idleTimeoutMs;
};

export const markSessionExpired = () => {
  sessionStorage.setItem("sessionExpiredMessage", SESSION_EXPIRED_MESSAGE);
};

export const getSessionExpiredMessage = () =>
  sessionStorage.getItem("sessionExpiredMessage") || "";

export const clearSessionExpiredMessage = () => {
  sessionStorage.removeItem("sessionExpiredMessage");
};

const authService = {
  register,
  login,
  refreshSession,
  logout,
  verifyEmail,
  resendVerification,
  forgotPassword,
  resetPassword,
  persistLogin,
  clearAuth,
  getStoredToken,
  getStoredRefreshToken,
  getStoredRole,
  getStoredUser,
  getTokenExpiryMs,
  isTokenExpired,
  willTokenExpireSoon,
  hasValidSession,
  touchSessionActivity,
  getLastActivityAt,
  isIdleTimedOut,
  markSessionExpired,
  getSessionExpiredMessage,
  clearSessionExpiredMessage,
};

export default authService;